/* eslint-disable no-underscore-dangle, camelcase */
import React, { useMemo } from 'react';
import styles from 'Components/PdfFormTemplates/PdfFormEditor/pdfFormEditor.module.css';
import { EditorField, TokenGroup } from '../types';
import { TokenPicker } from './TokenPicker';

const supportsFontSize = (field: EditorField) => field.field_type === 'text' || field.field_type === 'date' || field.field_type === 'email' || field.field_type === 'phone' || field.field_type === 'number';
const isMarkField = (field: EditorField) => field.field_type === 'signature' || field.field_type === 'initial';
const supportsUserFillable = (field: EditorField) => !isMarkField(field);
const supportsRequired = (field: EditorField) => !isMarkField(field) && field.user_fillable !== false;
const supportsNumericFormat = (field: EditorField) => field.field_type === 'number';

interface Props {
  fields: EditorField[];
  tokenGroups: TokenGroup[];
  onUpdate: (clientId: string, updates: Partial<EditorField>) => void;
  onBulkUpdate: (updates: Partial<EditorField>, predicate?: (field: EditorField) => boolean) => void;
  onDelete: (clientId: string) => void;
  onBulkDelete: () => void;
  hideHeader?: boolean;
}

export function FieldProperties({ fields, tokenGroups, onUpdate, onBulkUpdate, onDelete, onBulkDelete, hideHeader }: Props) {
  if (fields.length === 0) {
    return (
      <div className={styles.propertiesPanel}>
        {!hideHeader && <h3>Properties</h3>}
        <div className={styles.emptyProperties}>Select a field to edit its properties</div>
      </div>
    );
  }

  const tokenMap = useMemo(() => {
    const map = new Map<string, { type: string }>();
    tokenGroups.forEach((g) => g.tokens.forEach((t) => map.set(t.value, { type: t.type })));
    return map;
  }, [tokenGroups]);

  const dateTokenFilter = useMemo(
    () => (t: { type: string }) => t.type === 'date',
    []
  );

  const nonDateTokenFilter = useMemo(
    () => (t: { type: string }) => t.type !== 'date',
    []
  );

  // Single field selected — show full properties
  if (fields.length === 1) {
    const field = fields[0];
    const typeLabel = field.field_type.charAt(0).toUpperCase() + field.field_type.slice(1);

    const handleTokenChange = (fieldName: string) => {
      const token = tokenMap.get(fieldName);
      const updates: Partial<EditorField> = { field_name: fieldName };
      if (token) {
        const tokenType = token.type as EditorField['field_type'];
        if (tokenType !== field.field_type) {
          updates.field_type = tokenType;
          updates.numeric_format = tokenType === 'number' ? (field.numeric_format || 'number') : undefined;
        }
      }
      onUpdate(field._clientId, updates);
    };

    return (
      <div className={styles.propertiesPanel}>
        {!hideHeader && <h3>Field Properties</h3>}

        <div className={styles.propGroup}>
          <label>Type</label>
          <div className={styles.readOnlyValue}>{typeLabel}</div>
        </div>

        <div className={styles.propGroup}>
          <label>Name / Token</label>
          <TokenPicker
            value={field.field_name}
            onChange={handleTokenChange}
            tokenGroups={tokenGroups}
            tokenFilter={field.field_type === 'date' ? dateTokenFilter : nonDateTokenFilter}
            hideCustomName={field.field_type === 'date'}
          />
        </div>

        {supportsFontSize(field) && (
          <div className={styles.propGroup}>
            <label>Font Size (pt)</label>
            <input
              type="number"
              value={field.font_size || 12}
              min={6}
              max={72}
              onChange={(e) => onUpdate(field._clientId, { font_size: Number(e.target.value) })}
            />
          </div>
        )}

        {supportsNumericFormat(field) && (
          <div className={styles.propGroup}>
            <label>Format</label>
            <select
              value={field.numeric_format || 'number'}
              onChange={(e) => onUpdate(field._clientId, { numeric_format: e.target.value as EditorField['numeric_format'] })}
            >
              <option value="number">Number</option>
              <option value="currency">Currency</option>
            </select>
          </div>
        )}

        <div className={styles.propGroup}>
          <label>Page</label>
          <div className={styles.readOnlyValue}>{field.page}</div>
        </div>

        {!isMarkField(field) && (
          <div className={styles.propGroup}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={field.user_fillable !== false}
                onChange={(e) => onUpdate(field._clientId, { user_fillable: e.target.checked, ...(!e.target.checked ? { required: false } : {}) })}
              />
              User Fillable
            </label>
          </div>
        )}

        {!isMarkField(field) && field.user_fillable !== false && (
          <div className={styles.propGroup}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={!!field.required}
                onChange={(e) => onUpdate(field._clientId, { required: e.target.checked })}
              />
              Required
            </label>
          </div>
        )}

        <button type="button" className={styles.propDeleteBtn} onClick={() => onDelete(field._clientId)}>
          Delete Field
        </button>
      </div>
    );
  }

  // Multiple fields selected — show bulk edit properties
  const fontSizeFields = fields.filter(supportsFontSize);
  const userFillableFields = fields.filter(supportsUserFillable);
  const requiredFields = fields.filter(supportsRequired);
  const numericFormatFields = fields.filter(supportsNumericFormat);

  const hasFontSizeFields = fontSizeFields.length > 0;
  const hasUserFillableFields = userFillableFields.length > 0;
  const hasRequiredFields = requiredFields.length > 0;
  const hasNumericFormatFields = numericFormatFields.length > 0;

  // Compute shared values for display
  const fontSizes = hasFontSizeFields
    ? [...new Set(fontSizeFields.map((f) => f.font_size || 12))]
    : [];
  const sharedFontSize = fontSizes.length === 1 ? fontSizes[0] : undefined;

  const fillableValues = hasUserFillableFields
    ? [...new Set(userFillableFields.map((f) => f.user_fillable !== false))]
    : [];
  const sharedFillable = fillableValues.length === 1 ? fillableValues[0] : undefined;

  const requiredValues = hasRequiredFields
    ? [...new Set(requiredFields.map((f) => !!f.required))]
    : [];
  const sharedRequired = requiredValues.length === 1 ? requiredValues[0] : undefined;

  const numericFormats = hasNumericFormatFields
    ? [...new Set(numericFormatFields.map((f) => f.numeric_format || 'number'))]
    : [];
  const sharedNumericFormat = numericFormats.length === 1 ? numericFormats[0] : '';

  return (
    <div className={styles.propertiesPanel}>
      {!hideHeader && <h3>Field Properties</h3>}

      <div className={styles.propGroup}>
        <div className={styles.readOnlyValue}>{`${fields.length} fields selected`}</div>
      </div>

      {hasFontSizeFields && (
        <div className={styles.propGroup}>
          <label>Font Size (pt)</label>
          <input
            type="number"
            value={sharedFontSize ?? ''}
            placeholder={sharedFontSize === undefined ? 'Mixed' : undefined}
            min={6}
            max={72}
            onChange={(e) => {
              const v = Number(e.target.value);
              if (v >= 6) onBulkUpdate({ font_size: v }, supportsFontSize);
            }}
          />
        </div>
      )}

      {hasNumericFormatFields && (
        <div className={styles.propGroup}>
          <label>Format</label>
          <select
            value={sharedNumericFormat}
            onChange={(e) => {
              if (!e.target.value) return;
              onBulkUpdate({ numeric_format: e.target.value as EditorField['numeric_format'] }, supportsNumericFormat);
            }}
          >
            <option value="">Mixed</option>
            <option value="number">Number</option>
            <option value="currency">Currency</option>
          </select>
        </div>
      )}

      {hasUserFillableFields && (
        <div className={styles.propGroup}>
          <label className={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={sharedFillable ?? false}
              ref={(el) => {
                if (el) el.indeterminate = sharedFillable === undefined;
              }}
              onChange={(e) => onBulkUpdate({ user_fillable: e.target.checked, ...(!e.target.checked ? { required: false } : {}) }, supportsUserFillable)}
            />
            User Fillable
          </label>
        </div>
      )}

      {hasRequiredFields && (
        <div className={styles.propGroup}>
          <label className={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={sharedRequired ?? false}
              ref={(el) => {
                if (el) el.indeterminate = sharedRequired === undefined;
              }}
              onChange={(e) => onBulkUpdate({ required: e.target.checked }, supportsRequired)}
            />
            Required
          </label>
        </div>
      )}

      <button type="button" className={styles.propDeleteBtn} onClick={onBulkDelete}>
        {`Delete ${fields.length} Fields`}
      </button>
    </div>
  );
}
